import { Component, OnInit, HostListener } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MenuItem, MessageService } from 'primeng/api';
import { NavigationService } from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import { AdvancedSettingsService } from '../../../../service/advanced-settings.service';
import { AccountService } from '../../../../service/account.service';
import { CopyToClipboardService } from '../../../../service/utilities/copy-to-clipboard.service';
import { FeatureFlagGuard } from '../../../../feature-flag.guard';
import { AccountInterface } from '../../../../model/account.interface';

@Component({
    selector: 'auto-advanced-settings',
    templateUrl: './advanced-settings.component.html',
    styleUrls: ['./advanced-settings.component.scss'],
    standalone: false
})
export class AdvancedSettingsComponent implements OnInit {
  accountId: number;
  settingsSideNav: MenuItem[];
  // Data object passed down to child tabs via [data]
  advancedSettings: any;
  // Child forms
  adConfigForm: UntypedFormGroup;
  pageConfigForm: UntypedFormGroup;
  leadMgmtForm: UntypedFormGroup;
  serviceCategoriesForm: UntypedFormGroup;
  generalForm: UntypedFormGroup;
  account: AccountInterface;
  preCacheBusy = false;
  saveSettingsLoading = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    protected copyToClipboardService: CopyToClipboardService,
    protected featureFlagGuard: FeatureFlagGuard,
    private advancedSettingsService: AdvancedSettingsService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Advanced');
    this.route.paramMap.subscribe((params) => {
      this.accountId = parseInt(params.get('accountId'));
      this.initNavigation();
    });
    this.advancedSettingsService.getAdvancedSettings(this.accountId).subscribe({
      next: (settings) => {
        this.accountService.getAccount(this.accountId).subscribe({
          next: (account) => {
            this.advancedSettings = settings;
            this.account = account;
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load advanced settings'
            });
          },
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load advanced settings'
        });
      },
    });
  }


  /**
   * Initialize the side navigation menu
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  // Child form initializers
  onAdConfigFormGroupInitialized(form: UntypedFormGroup) {
    this.adConfigForm = form;
  }

  onPageConfigFormGroupInitialized(form: UntypedFormGroup) {
    this.pageConfigForm = form;
  }

  onLeadManagementFormGroupInitialized(form: UntypedFormGroup) {
    this.leadMgmtForm = form;
  }

  onServiceCategoriesFormGroupInitialized(form: UntypedFormGroup) {
    this.serviceCategoriesForm = form;
  }

  onGeneralFormGroupInitialized(form: UntypedFormGroup) {
    this.generalForm = form;
  }

  getSaveButtonDisabled(): boolean {
    const forms = [
      this.adConfigForm,
      this.pageConfigForm,
      this.leadMgmtForm,
      this.serviceCategoriesForm,
      this.generalForm
    ].filter(Boolean) as UntypedFormGroup[];

    const anyDirtyAndValid = forms.some((form) => form.dirty && form.valid);
    return !anyDirtyAndValid;
  }

  saveAdvancedSettings() {
    this.saveSettingsLoading = true;
    const updatedAdvancedSettings = this.buildUpdatedAdvancedSettings();
    this.advancedSettingsService.updateAdvancedSettings(updatedAdvancedSettings).subscribe({
      next: () => {
        this.saveSettingsLoading = false;
        [
          this.adConfigForm,
          this.pageConfigForm,
          this.leadMgmtForm,
          this.serviceCategoriesForm,
          this.generalForm
        ]
          .filter(Boolean)
          .forEach((f: UntypedFormGroup) => f.markAsPristine());
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Advanced settings saved successfully'
        });
      },
      error: () => {
        this.saveSettingsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save advanced settings'
        });
      }
    });
  }

  /**
   * Build the updatedAdvancedSettings object to be sent to the server
   * @returns {any}
   */

  private buildUpdatedAdvancedSettings(): any {
    const adConfigValue = this.adConfigForm?.getRawValue() || {};
    const pageConfigValue = this.pageConfigForm?.getRawValue() || {};
    const leadMgmtValue = this.leadMgmtForm?.getRawValue() || {};
    const serviceCatgoriesValues = this.serviceCategoriesForm?.getRawValue() || {};
    const generalFormValue = this.generalForm?.getRawValue() || {};

    return {
      accountId: this.accountId,

      // Offer Set Configuration
      accountOfferSetRefresh: !!adConfigValue.accountOfferSetRefresh,
      customTitleDisplayOverride: !!adConfigValue.customTitleDisplayOverride,
      newOfferSetCustomTitleOverrideText: String(adConfigValue.newOfferSetCustomTitleOverrideText ?? ''),
      usedOfferSetCustomTitleOverrideText: String(adConfigValue.usedOfferSetCustomTitleOverrideText ?? ''),
      cpoOfferSetCustomTitleOverrideText: String(adConfigValue.cpoOfferSetCustomTitleOverrideText ?? ''),
      minimizeDisclaimer: !!adConfigValue.minimizeDisclaimer,
      newMainCtaText: String(adConfigValue.newMainCtaText ?? ''),
      usedMainCtaText: String(adConfigValue.usedMainCtaText ?? ''),
      disclosureSet: Number.isFinite(adConfigValue.disclosureSet) ? Number(adConfigValue.disclosureSet) : 0,
      enhancedDisclosure: !!adConfigValue.enhancedDisclosure,
      sharedOfferSetsEnabled: !!adConfigValue.sharedOfferSetsEnabled,

      // pageConfigValue Configuration
      newUsedWhyBuyMessage: String(pageConfigValue.whyBuyMessage ?? ''),
      serviceWhyBuyMessage: String(pageConfigValue.serviceWhyBuyMessage ?? ''),
      dealerLicenseName: String(pageConfigValue.dealerLicenseName ?? ''),
      ardNumber: String(pageConfigValue.ardNumber ?? ''),
      servicePhoneNumber: String(pageConfigValue.servicePhoneNumber ?? ''),
      serviceDisclosure: String(pageConfigValue.serviceDisclosure ?? ''),
      pageHeaderMainCtaText: String(pageConfigValue.pagesMainCtaText ?? ''),
      headerMainCtaText: String(pageConfigValue.inventoryMainCtaText ?? ''),
      showUsedOnNewVlps: !!pageConfigValue.newVlpShowUsed,
      showFormWhenNoOfferSetsAvailable: !!pageConfigValue.showDefaultForm,

      // Lead Management
      ccpaConsentEnabled: !!leadMgmtValue.leadFormConsentEnabled,
      ccpaConsentText: String(leadMgmtValue.leadFormConsentText ?? ''),
      tcpaConsentEnabled: !!leadMgmtValue.detailedLeadFormConsentEnabled,
      tcpaConsentText: String(leadMgmtValue.detailedLeadFormConsentText ?? ''),
      newFormMainCtaText: String(leadMgmtValue.newFormMainCtaText ?? ''),
      usedFormMainCtaText: String(leadMgmtValue.usedFormMainCtaText ?? ''),
      requirePostalCodeOnLeadForms: !!leadMgmtValue.leadsNeedPostalCode,
      requirePreferredContactOnLeadForm: !!leadMgmtValue.preferredContactRequired,
      leadFormTfaEnabled: !!leadMgmtValue.leadFormTfaEnabled,

      // Service Categories
      accountAvailableServiceCategories: Array.isArray(serviceCatgoriesValues.accountAvailableServiceCategories)
      ? serviceCatgoriesValues.accountAvailableServiceCategories
        : [],

      // General
      preCacheSpecials: !!generalFormValue.preCacheSpecials,
      emailToolVersion: Number.isFinite(generalFormValue.emailVersion) ? Number(generalFormValue.emailVersion) : 1,
      enhancedSeoEnabled: !!generalFormValue.enhancedSeoEnabled,
      inAcuraProgram: !!generalFormValue.inAcuraProgram,
      inAudiProgram: !!generalFormValue.inAudiProgram,
      inHondaProgram: !!generalFormValue.inHondaProgram,
      inFordDirectProgram: !!generalFormValue.inFordDirectProgram,
      fordDirectDealerId: String(generalFormValue.fordDirectDealerId ?? ''),
      stellantisReportingTag: !!generalFormValue.stellantisReportingTag,
      showDashboardReport: !!generalFormValue.showDashboardReport,
      adobeTaggingEnabled: !!generalFormValue.adobeTaggingEnabled,
      dynamicCreativeEnabled: !!generalFormValue.dynamicCreativeEnabled,
      mediaAdCopyBulkUpdateEnabled: !!generalFormValue.mediaAdCopyBulkUpdateEnabled,
    };
  }
  onPreCacheRequested(): void {
    if (!this.preCacheBusy) {
      this.preCacheBusy = true;

      this.advancedSettingsService.refreshCache(this.accountId).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Pre Cache Validation',
            detail: 'Cache refresh completed.'
          });
          this.advancedSettingsService.getAdvancedSettings(this.accountId).subscribe({
            next: (settings) => (this.advancedSettings = settings)
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Pre Cache Validation',
            detail: 'Failed to refresh cache.'
          });
        },
        complete: () => {
          this.preCacheBusy = false;
        }
      });
    }
  }

  private hasAnyUnsavedChanges(): boolean {
    const forms = [
      this.adConfigForm,
      this.pageConfigForm,
      this.leadMgmtForm,
      this.serviceCategoriesForm,
      this.generalForm,
    ].filter(Boolean) as UntypedFormGroup[];

    return forms.some((form) => form.dirty);
  }

  // Show native prompt on browser/tab refresh or close
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasAnyUnsavedChanges()) {
      const message = 'Are you sure you want to leave this page? Unsaved changes will be lost.';
      event.returnValue = message;
    }
  }
}
